import JsSIP from 'jssip';
import type { UA } from 'jssip';
import type { ConnectedEvent, RegisteredEvent, UnRegisteredEvent } from 'jssip/lib/UA';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { IncomingRequest, IncomingResponse } from 'jssip/lib/SIPMessage';
import axios from 'axios';
import {
  AgentConfig,
  SIPCredentials,
  CallMeta,
  VolumeInfo,
  TaarWebRTCConfig,
  TaarWebRTCState,
  TaarWebRTCMethods,
  OptionsContentFormat,
  PassedMessage,
  AutoDialRequest,
  AutoDialResponse,
  AutoDialRequestStatus,
} from '../types';
import { WebRTCConfigurationResponse } from '../types/WebRTCConfigurationResponse';
import { parse } from 'sip-parser';
import { EventEmitter } from 'events';
import { TaarWebRTCEvents, getLogger, initializeLogger, UnifiedLogger } from './logger';
import { loadCreds, storeCreds } from './secure';

declare const __APP_VERSION__: string;

export class TaarWebRTC implements TaarWebRTCMethods {
  private ua: UA | null = null;
  private session: RTCSession | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private state: TaarWebRTCState = {
    isRegistered: false,
    isCallActive: false,
    isReady: false,
    isMuted: false,
    volume: { input: 100, output: 100 },
  };
  private config: TaarWebRTCConfig;
  private credentials: WebRTCConfigurationResponse | null = null;
  private logger: UnifiedLogger;
  private _lastNetworkState: boolean | null = null;
  private _lastUAState: { connected: boolean; registered: boolean } | null = null;

  constructor(config: TaarWebRTCConfig = {}) {
    if(config.prefix === undefined) {
      config = {
        ...config,
        prefix: "https://api.taar.ai"
      }
    }

    this.config = config;
    const loggerInstance = getLogger();
    if (!loggerInstance) {
      throw new Error('Logger not initialized');
    }
    this.logger = loggerInstance;
    
    if (config.debug) {
      JsSIP.debug.enable('JsSIP:*');
      this.logger.setDebugMode(true);
    }

    this.fetchNonceAndSignalReady();
    
    // Set up automatic cleanup on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', async () => {
        await (this.logger as any).flushAllLogs();
      });
    }
  }

  private emitter = new EventEmitter();

  private async fetchCredentials(agentCredentials: string): Promise<WebRTCConfigurationResponse> {
    try {
      const response = await axios.post(`${this.config.prefix}/agent/config`, {
        agentCredentials,
      }, {
        headers: {
          'X-Taar-Version': __APP_VERSION__,
          'Authorization': `Bearer ${agentCredentials}`
        }
      });
      const webRTCConfiguration = response.data as WebRTCConfigurationResponse;
      storeCreds({jwt: agentCredentials})
      return webRTCConfiguration
    } catch (error) {
      throw new Error(`Failed to fetch SIP credentials: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private updateState(partial: Partial<TaarWebRTCState>) {
    const previousState = { ...this.state };
    this.state = { ...this.state, ...partial };
    
    // Detect registration state changes
    if (previousState.isRegistered !== this.state.isRegistered) {
      this.logger.log("Registration state changed", {
        callId: this.session?.id || 'state-change',
        previousRegistered: previousState.isRegistered,
        currentRegistered: this.state.isRegistered,
        event: this.state.isRegistered ? TaarWebRTCEvents.REGISTRATION_STATE_TRUE : TaarWebRTCEvents.REGISTRATION_STATE_FALSE
      });
    }
    
    this.config.onStateChange?.(this.state);
  }

  private handleRegistered(event: RegisteredEvent) {
    const callId = this.session?.id || 'no-active-call';
    const wasPreviouslyRegistered = this.state.isRegistered;
    const isReRegistration = wasPreviouslyRegistered;
    
    this.logger.log("Registration successful", {
      callId,
      response: event.response,
      status: event.response?.status_code,
      isReRegistration,
      event: isReRegistration ? TaarWebRTCEvents.RE_REGISTRATION_SUCCESSFUL : TaarWebRTCEvents.INITIAL_REGISTRATION_SUCCESSFUL
    });
    
    this.updateState({ isRegistered: true, error: undefined });
  }

  private handleConnected(event: ConnectedEvent) {
    const callId = this.session?.id || 'no-active-call';
    this.logger.log("Connected to WebRTC", {
      callId,
      socket: event.socket,
      event: TaarWebRTCEvents.CONNECTED_TO_WEBRTC
    });
  }

  private handleUnregistered(event: UnRegisteredEvent) {
    const callId = this.session?.id || 'no-active-call';
    this.logger.log("Unregistered", {
      callId,
      cause: event.cause,
      response: event.response,
      event: TaarWebRTCEvents.UNREGISTERED
    });
    this.updateState({ isRegistered: false });
    this.config.onDisconnect?.();
  }

  private async handleRegistrationFailed(event: UnRegisteredEvent) {
    const callId = this.session?.id || 'no-active-call';
    const wasPreviouslyRegistered = this.state.isRegistered;
    
    this.logger.error("Registration failed", {
      callId,
      cause: event.cause,
      response: event.response,
      wasPreviouslyRegistered,
      event: wasPreviouslyRegistered ? TaarWebRTCEvents.RE_REGISTRATION_FAILED : TaarWebRTCEvents.REGISTRATION_FAILED
    });
    
    this.updateState({ 
      isRegistered: false, 
      error: new Error(`Registration failed: ${event.cause || 'Unknown cause'}`) 
    });
    
    this.config.onError?.(new Error(`Registration failed: ${event.cause || 'Unknown cause'}`));
    
    // Flush logs after error events
    await this.flushPendingLogs();
  }

  private setupAudioStreams(session: RTCSession) {
    const callId = session.id;
    
    if(!session.connection) {
      this.logger.warn("No session connection available", { 
        callId,
        event: TaarWebRTCEvents.NO_SESSION_CONNECTION
      });
      return;
    }
    
    const remoteStreams = session.connection.getRemoteStreams();

    if (remoteStreams && remoteStreams.length > 0 && this.audioElement) {
      const remoteStream = remoteStreams[0];
      this.audioElement.srcObject = remoteStream;
      this.audioElement.volume = this.state.volume.output / 100;
      
      this.logger.log("Audio element configured", {
        callId,
        volume: this.audioElement.volume,
        muted: this.audioElement.muted,
        readyState: this.audioElement.readyState,
        event: TaarWebRTCEvents.AUDIO_ELEMENT_CONFIGURED
      });
    } else {
      this.logger.warn("No remote streams or audio element", {
        callId,
        hasRemoteStreams: !!(remoteStreams && remoteStreams.length > 0),
        hasAudioElement: !!this.audioElement,
        event: TaarWebRTCEvents.NO_REMOTE_STREAMS
      });
    }
  }

  private handleProgress(event: any) {
    const callId = this.session?.id || 'no-active-call';
    this.logger.log("Call progress", {
      callId,
      eventType: event.type,
      response: event.response,
      event: TaarWebRTCEvents.CALL_PROGRESS
    });
    
    if (this.session) {
      this.setupAudioStreams(this.session);
    }
  }

  private handleCallAccepted(event: any) {
    const callId = this.session?.id || event.response?.call_id || 'unknown-call';
    
    try {
      const currentCall = {
        id: callId,
        remoteIdentity: event.response?.headers?.To?.[0]?.raw ?? 'unknown',
        startTime: new Date(),
        status: 'connected' as 'connected',
      }
      
      if (this.session) {
        this.setupAudioStreams(this.session);
      }

      this.updateState({
        isCallActive: true,
        currentCall,
      });
      this.config.onCallStarted?.(currentCall);
      
      this.logger.log("Call state updated to active", {
        callId,
        isCallActive: true,
        currentCall: currentCall,
        event: TaarWebRTCEvents.CALL_STATE_ACTIVE
      });
    }
    catch(e) {
      this.logger.error("Error in handling call acceptance", {
        callId,
        error: e,
        event: TaarWebRTCEvents.HANDLE_CALL_ACCEPTANCE_ERROR
      });
      this.updateState({ error: e instanceof Error ? e : new Error('Failed to handle call acceptance') });
    }
  }

  private async handleCallEnded(event: any) {
    const callId = this.session?.id || 'unknown-call';
    this.logger.log("Call ended", {
      callId,
      eventType: event.type,
      response: event.response,
      event: TaarWebRTCEvents.CALL_ENDED
    });
    
    this.updateState({ 
      isCallActive: false,
      currentCall: undefined,
    });
    this.config.onCallEnded?.();
    
    this.session = null;
    
    // Flush logs after important events
    await this.flushPendingLogs();
  }

  private async handleCallFailed(event: any) {
    const session = event.session;
    const callId = session?.id || this.session?.id || 'unknown-call';
    
    const callInfo: CallMeta = {
      id: callId,
      remoteIdentity: session?.remote_identity?.uri?.toString() || 'unknown',
      startTime: new Date(),
      status: 'failed',
      error: event.cause || 'Unknown cause',
    };
    
    this.updateState({ 
      isCallActive: false,
      currentCall: callInfo,
      error: new Error(callInfo.error),
    });
    this.config.onCallEnded?.();
    this.config.onError?.(new Error(callInfo.error));
    
    this.session = null;
    
    // Flush logs after error events
    await this.flushPendingLogs();
  }

  private handleIncomingCall(event: any) {
    const callId = event.session?.id || 'unknown-call';
    
    this.logger.log("Handling incoming call event", {
      callId,
      originator: event.originator,
      hasSession: !!event.session,
      event: TaarWebRTCEvents.INCOMING_CALL_RECEIVED
    });
    
    // Only handle incoming calls
    if (event.originator === 'remote' && event.session) {
      this.session = event.session;

      this.logger.log("Processing incoming call", {
        callId: this.session?.id || 'unknown-call',
        remoteIdentity: this.session?.remote_identity?.uri?.toString() || 'unknown',
        event: TaarWebRTCEvents.INCOMING_CALL_PROCESSING
      });

      // Set up event handlers for the incoming call
      if (this.session) {
        this.session.on('peerconnection', (e: any) => {
          if (e.peerconnection) {
            this.setupAudioStreams(this.session!);
          }
        });

        this.session.on('progress', (e: any) => this.handleProgress(e));
        this.session.on('accepted', (e: any) => this.handleCallAccepted(e));
        this.session.on('ended', (e: any) => {
          this.handleCallEnded(e).catch(error => {
            this.logger.error('[TaarWebRTC] Error in call ended handler:', { error });
          });
        });
        this.session.on('failed', (e: any) => {
          this.handleCallFailed(e).catch(error => {
            this.logger.error('[TaarWebRTC] Error in call failed handler:', { error });
          });
        });

        // Notify about incoming call
        const incomingCallInfo = {
          id: this.session.id,
          remoteIdentity: this.session.remote_identity.uri.toString(),
          startTime: new Date(),
          status: 'incoming' as const,
        };
        
        this.logger.log("Incoming call notification", {
          callId: this.session.id,
          incomingCallInfo,
          event: TaarWebRTCEvents.INCOMING_CALL_NOTIFICATION
        });
        
        this.config.onIncomingCall?.(incomingCallInfo);
      }
    } else {
      this.logger.log("Ignoring non-remote call", {
        callId,
        originator: event.originator,
        event: TaarWebRTCEvents.IGNORING_NON_REMOTE_CALL
      });
    }
  }

  onMessage<T = any>(event: string, handler: (data: T) => void) {
    this.emitter.on(event, handler);
  }

  async register(agentCredentials: string, audioElement?: HTMLAudioElement): Promise<void> {
    const callId = this.session?.id || 'registration-process';
    
    try {
      // Set the audio element if provided
      if (audioElement) {
        this.audioElement = audioElement;
        this.audioElement.autoplay = true;
      }

      this.credentials = await this.fetchCredentials(agentCredentials);
      
      // Configure batch logging if logGatewayUrl is available
      if (this.credentials.data.logGatewayUrl) {
        this.logger.configureBatchLogging({
          logGatewayUrl: this.credentials.data.logGatewayUrl,
          agentCredentials: agentCredentials,
          enableBatchLogging: true,
          batchSize: 50,
          batchTimeout: 5000
        });
        
        this.logger.log("Batch logging configured", {
          callId,
          logGatewayUrl: this.credentials.data.logGatewayUrl,
          event: TaarWebRTCEvents.CREDENTIALS_FETCHED
        });
      }
      
      const socket = new JsSIP.WebSocketInterface(this.credentials.data.socketUrl);

      const configuration = {
        sockets: [socket],
        uri: this.credentials.data.sipUri,
        password: this.credentials.data.sipPassword,
        register: this.credentials.data.shouldRegister,
        register_expires: this.credentials.data.registerExpiresIn,
        session_timers: this.credentials.data.sessionTimersEnabled,
        pcConfig: {...this.credentials.data.peerConnection}, 
      };

      this.ua = new JsSIP.UA(configuration);
      
      // Set up event handlers
      this.ua.on('connected', (e: ConnectedEvent) => this.handleConnected(e));
      this.ua.on('registered', (e: RegisteredEvent) => this.handleRegistered(e));
      this.ua.on('unregistered', (e: UnRegisteredEvent) => this.handleUnregistered(e));
      this.ua.on('registrationFailed', (e: UnRegisteredEvent) => {
        this.handleRegistrationFailed(e).catch(error => {
          this.logger.error('[TaarWebRTC] Error in registrationFailed handler:', { error });
        });
      });

      this.ua.on('newOptions', (data: any) => {
        const raw = data.message._request.data;
        const parsed = parse(raw);
        const headers: any = {};
        parsed.headers.forEach((r) => {
          headers[r.fieldName] = r.fieldValue;
        });
        const parsedContent = JSON.parse(parsed.content) as OptionsContentFormat;
        this.emitter.emit(parsedContent.namespace, parsedContent.data);
      });

      this.ua.on('newMessage', (data: any) => {
        const raw = data.message._request.data;
        const parsed = parse(raw);
        const headers: any = {};
        parsed.headers.forEach((r) => {
          headers[r.fieldName] = r.fieldValue;
        });
        const parsedContent = JSON.parse(parsed.content) as OptionsContentFormat;
        this.emitter.emit(parsedContent.namespace, parsedContent.data);
      });

      // Explicitly bind the incoming call handler
      const boundHandleIncomingCall = this.handleIncomingCall.bind(this);
      this.ua.on('newRTCSession', (e: { originator: string; session: RTCSession }) => {
        boundHandleIncomingCall(e);
        const attach = () => {
          const pc = e.session.connection;
          if (!pc) return;
      
          // hook PC events here
          pc.addEventListener('icecandidateerror', (e) => {
            console.log("ice candidate errorr")
          });
          pc.addEventListener('icecandidate', (e: RTCPeerConnectionIceEvent) => {
            const cand = e.candidate?.candidate || '';
            if (/ 10\.5\.99\./.test(cand) || / 169\.254\./.test(cand)) {
              // swallow this candidate; don't signal it
              console.log("Swallow candidate and return", cand);
              return;
            } else {
              console.log("Trying candidate", cand);
            }
            // otherwise, let JsSIP signal as usual
          });
          pc.addEventListener('iceconnectionstatechange', () => {console.log("ICE Connection State Change")});
          console.log('PC ready for', e.session.id);
        };
      
        // if PC already exists, attach now; also catch the event if it’s not yet created
        if (e.session.connection) attach();
        e.session.once('peerconnection', attach);
      });

      await this.ua.start();
      
    } catch (error) {
      this.logger.error("Registration failed", {
        callId,
        error: error
      });
      const err = error instanceof Error ? error : new Error('Failed to register');
      this.updateState({ error: err });
      this.config.onError?.(err);
      throw err;
    }
  }

  async unregister(): Promise<void> {
    const callId = this.session?.id || 'unregister-process';
    
    if (!this.ua) {
      throw new Error('UA not initialized');
    }
    
    await this.ua.unregister();
    this.ua.stop();
    this.ua = null;
    
    this.updateState({ isRegistered: false });
    
    // Flush any pending logs before cleanup
    await this.flushPendingLogs();
  }

  private async flushPendingLogs(): Promise<void> {
    try {
      await (this.logger as any).flushAllLogs();
    } catch (error) {
      // Don't throw errors during cleanup
      this.logger.warn('[TaarWebRTC] Failed to flush logs during cleanup:', { error });
    }
  }

  async autoDial(autoDialRequest: AutoDialRequest): Promise<AutoDialResponse> {
    console.log("Autodial Targets", autoDialRequest)
    const agentCreds = loadCreds()
    if(agentCreds == null) {
      throw Error("Agent Credentials are not defined. Please re-register")
    }
    const response = await axios.post(`${this.config.prefix}/dialer/targets`, {
      ...autoDialRequest,
    }, {
      headers: {
        'X-Taar-Version': __APP_VERSION__,
        'Authorization': `Bearer ${agentCreds.jwt}`
      }
    });

    if(response.status == 200) {
      const r = response.data as AutoDialResponse
      if(r.status === AutoDialRequestStatus.ACCEPTED) {
        this.makeCall("autodialer")
      }
      return r
    } else if(response.status == 500) {
      return { status: AutoDialRequestStatus.SERVER_ERROR }
    } else if(response.status == 401) {
      return { status: AutoDialRequestStatus.INVALID_CREDENTIALS }
    } else if(response.status == 403) {
      return { status: AutoDialRequestStatus.NOT_ALLOWED }
    }

    return { status: AutoDialRequestStatus.UNKNOWN }
  }

  async makeCall(to: string): Promise<void> {
    const callId = this.session?.id || 'outgoing-call';
    
    this.logger.log("Making call", {
      callId,
      to,
      event: TaarWebRTCEvents.MAKE_CALL_STARTED
    });
    
    if (!this.ua) {
      this.logger.error("UA not initialized for call", {
        callId,
        to,
        event: TaarWebRTCEvents.MAKE_CALL_UA_NOT_INITIALIZED
      });
      throw new Error('UA not initialized');
    }
    if (this.session) {
      this.logger.error("Call already in progress", {
        callId,
        to,
        event: TaarWebRTCEvents.MAKE_CALL_ALREADY_IN_PROGRESS
      });
      throw new Error('Call already in progress');
    }

    const options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: { 
        ...this.credentials?.data.peerConnection, 
        iceTransportPolicy: 'relay' as const,
        iceCandidatePoolSize: 2,
        // bundlePolicy: 'max-bundle' as const,
        rtcpMuxPolicy: 'require' as const
      }      
    };

    this.logger.log("Call options configured", {
      callId,
      to,
      options,
      event: TaarWebRTCEvents.MAKE_CALL_OPTIONS_CONFIGURED
    });

    try {
      this.session = this.ua.call(to, options);

      this.session.on('peerconnection', (e: any) => {
        console.log("Test peer connection", e);
        this.logger.log("Outgoing call peer connection", {
          callId: this.session?.id,
          to,
          event: TaarWebRTCEvents.OUTGOING_CALL_PEER_CONNECTION
        });
        // Add peer connection monitoring if needed
      });
      
      this.logger.log("Call initiated", {
        callId: this.session.id,
        to,
        sessionId: this.session.id,
        event: TaarWebRTCEvents.MAKE_CALL_INITIATED
      });
      
      this.updateState({
        isCallActive: true,
        currentCall: {
          id: this.session.id,
          remoteIdentity: to,
          startTime: new Date(),
          status: 'connecting',
        },
      });

      this.logger.log("Setting up outgoing call event handlers", {
        callId: this.session.id,
        to,
        event: TaarWebRTCEvents.MAKE_CALL_EVENT_HANDLERS_SETUP
      });
      this.session.on('progress', (e: any) => this.handleProgress(e));
      this.session.on('accepted', (e: any) => this.handleCallAccepted(e));
      this.session.on('ended', (e: any) => {
        this.handleCallEnded(e).catch(error => {
          this.logger.error('[TaarWebRTC] Error in call ended handler:', { error });
        });
      });
      this.session.on('failed', (e: any) => {
        this.handleCallFailed(e).catch(error => {
          this.logger.error('[TaarWebRTC] Error in call failed handler:', { error });
        });
      });
    
    } catch (error) {
      this.logger.error("Failed to make call", {
        callId,
        to,
        error: error
      });
      const err = error instanceof Error ? error : new Error('Failed to make call');
      this.updateState({ error: err });
      this.config.onError?.(err);
      throw err;
    }
  }

  async endCall(): Promise<void> {
    const callId = this.session?.id || 'unknown-call';
    
    this.logger.log("Ending call", {
      callId,
      event: TaarWebRTCEvents.END_CALL_STARTED
    });
    
    if (!this.session) {
      this.logger.warn("No active call to end", {
        callId,
        event: TaarWebRTCEvents.END_CALL_NO_ACTIVE_CALL
      });
      throw new Error('No active call');
    }
    
    // Clean up audio element
    if (this.audioElement) {
      this.logger.log("Cleaning up audio element", {
        callId,
        event: TaarWebRTCEvents.END_CALL_AUDIO_CLEANUP
      });
      this.audioElement.srcObject = null;
    }
    
    this.logger.log("Terminating session", {
      callId,
      event: TaarWebRTCEvents.END_CALL_SESSION_TERMINATION
    });
    
    await this.session.terminate();
    
    this.logger.log("Call ended successfully", {
      callId,
      event: TaarWebRTCEvents.END_CALL_SUCCESSFUL
    });
  }

  mute(): void {
    const callId = this.session?.id || 'unknown-call';
    
    this.logger.log("Muting call", {
      callId,
      event: TaarWebRTCEvents.MUTE_CALL_STARTED
    });
    
    if (!this.session) {
      this.logger.warn("No active call to mute", {
        callId,
        event: TaarWebRTCEvents.MUTE_CALL_NO_ACTIVE_CALL
      });
      throw new Error('No active call');
    }
    
    this.session.mute();
    this.logger.log("Mute session complete", {
      callId,
      event: TaarWebRTCEvents.MUTE_SESSION_COMPLETE
    });
    
    this.updateState({ isMuted: true });
    this.logger.log("Mute state updated", {
      callId,
      event: TaarWebRTCEvents.MUTE_STATE_UPDATED
    });
    
    this.config.onMute?.();
  }

  unmute(): void {
    const callId = this.session?.id || 'unknown-call';
    
    this.logger.log("Unmuting call", {
      callId,
      event: TaarWebRTCEvents.UNMUTE_CALL_STARTED
    });
    
    if (!this.session) {
      this.logger.warn("No active call to unmute", {
        callId,
        event: TaarWebRTCEvents.UNMUTE_CALL_NO_ACTIVE_CALL
      });
      throw new Error('No active call');
    }
    
    this.session.unmute();
    this.logger.log("Unmute session complete", {
      callId,
      event: TaarWebRTCEvents.UNMUTE_SESSION_COMPLETE
    });
    
    this.updateState({ isMuted: false });
    this.logger.log("Unmute state updated", {
      callId,
      event: TaarWebRTCEvents.UNMUTE_STATE_UPDATED
    });
    
    this.config.onUnmute?.();
  }

  async sendDTMF(tones: string, duration: number = 100, interToneGap: number = 70): Promise<void> {
    const callId = this.session?.id || 'unknown-call';
    
    this.logger.log("Sending DTMF tones via RFC 4733", {
      callId,
      tones,
      duration,
      interToneGap,
      event: TaarWebRTCEvents.SEND_DTMF_STARTED
    });
    
    if (!this.session) {
      this.logger.warn("No active call to send DTMF", {
        callId,
        tones,
        event: TaarWebRTCEvents.SEND_DTMF_NO_ACTIVE_CALL
      });
      throw new Error('No active call');
    }
    
    if (!this.session.connection) {
      this.logger.warn("No peer connection available for DTMF", {
        callId,
        tones,
        event: TaarWebRTCEvents.SEND_DTMF_NO_ACTIVE_CALL
      });
      throw new Error('No peer connection available');
    }
    
    // Validate DTMF tones (only allow valid DTMF characters)
    const validDTMFChars = /^[0-9*#ABCD]+$/;
    if (!validDTMFChars.test(tones)) {
      this.logger.error("Invalid DTMF tones", {
        callId,
        tones,
        event: TaarWebRTCEvents.SEND_DTMF_INVALID_TONES
      });
      throw new Error('Invalid DTMF tones. Only 0-9, *, #, A, B, C, D are allowed.');
    }
    
    try {
      // Use WebRTC RTCDTMFSender for RFC 4733 compliance
      const peerConnection = this.session.connection;
      const senders = peerConnection.getSenders();
      const audioSender = senders.find(sender => 
        sender.track && sender.track.kind === 'audio'
      );
      
      if (!audioSender || !audioSender.dtmf) {
        this.logger.error("DTMF not supported or audio sender not found", {
          callId,
          tones,
          hasAudioSender: !!audioSender,
          hasDTMF: !!(audioSender && audioSender.dtmf),
          event: TaarWebRTCEvents.SEND_DTMF_FAILED
        });
        throw new Error('DTMF not supported or audio sender not found');
      }
      
      // Send DTMF using RFC 4733 (RTP payload)
      audioSender.dtmf.insertDTMF(tones, duration, interToneGap);
      
      this.logger.log("DTMF tones sent successfully via RFC 4733", {
        callId,
        tones,
        duration,
        interToneGap,
        method: 'RFC 4733 (RTP payload)',
        event: TaarWebRTCEvents.SEND_DTMF_SUCCESSFUL
      });
      
    } catch (error) {
      this.logger.error("Failed to send DTMF tones via RFC 4733", {
        callId,
        tones,
        duration,
        interToneGap,
        method: 'RFC 4733 (RTP payload)',
        error: error,
        event: TaarWebRTCEvents.SEND_DTMF_FAILED
      });
      
      const err = error instanceof Error ? error : new Error('Failed to send DTMF tones via RFC 4733');
      this.config.onError?.(err);
      throw err;
    }
  }

  setVolume(volume: Partial<VolumeInfo>): void {
    const callId = this.session?.id || 'no-active-call';
    
    this.logger.log("Setting volume", {
      callId,
      volume,
      event: TaarWebRTCEvents.SET_VOLUME_STARTED
    });
    
    const newVolume = { ...this.state.volume, ...volume };
    
    this.updateState({ volume: newVolume });
    
    // Update audio element volume if it exists
    if (this.audioElement && 'output' in volume) {
      this.audioElement.volume = volume.output! / 100;
      this.logger.log("Audio element volume updated", {
        callId,
        newVolume,
        event: TaarWebRTCEvents.SET_VOLUME_AUDIO_ELEMENT_UPDATED
      });
    }
    
    this.logger.log("Volume state updated", {
      callId,
      newVolume,
      event: TaarWebRTCEvents.SET_VOLUME_STATE_UPDATED
    });
    
    this.config.onVolumeChange?.(newVolume);
  }

  getState(): TaarWebRTCState {
    return { ...this.state };
  }

  async answerCall(): Promise<void> {
    console.log("Answering call", this.credentials)
    const callId = this.session?.id || 'unknown-call';
    if (!this.session) throw new Error('No incoming call to answer');
  
    // Build pcConfig safely
    const basePc = this.credentials?.data?.peerConnection;
    const iceServers = Array.isArray(basePc?.iceServers) ? basePc.iceServers : [];
    const pcConfig: RTCConfiguration = {
      ...basePc,
      // force relay ONLY if TURN is configured; otherwise omit while debugging
      ...(iceServers.length > 0 ? { iceTransportPolicy: 'relay' as const } : {}),
      iceCandidatePoolSize: 2,
      rtcpMuxPolicy: 'require' as const
    };
  
    // Install handlers BEFORE answer() so they catch the created RTCPeerConnection
    const onPeerConn = ({ peerconnection }: any) => {
      const pc: RTCPeerConnection = peerconnection || (this.session as any).connection;
  
      // Ensure there's at least one audio m-line no matter what (prevents empty SDP)
      if (!pc.getTransceivers().some(t => t.receiver?.track?.kind === 'audio')) {
        pc.addTransceiver('audio', { direction: 'recvonly' });
      }
  
      // Helpful visibility while debugging
      pc.onicegatheringstatechange = () => this.logger.info('iceGathering', {
        callId, state: pc.iceGatheringState
      });
      pc.oniceconnectionstatechange = () => this.logger.info('iceConnection', {
        callId, state: pc.iceConnectionState
      });
      pc.onicecandidate = (e) => this.logger.info('iceCandidate', {
        callId, candidate: e.candidate ? e.candidate.candidate : null
      });
    };
  
    // one-time listener
    (this.session as any).once?.('peerconnection', onPeerConn);
  
    try {
      const options = {
        // If mic is allowed this becomes sendrecv; if denied, we still have recvonly from the transceiver.
        mediaConstraints: { audio: true, video: false },
        pcConfig,
        // Legacy hint (harmless on modern stacks; helps some):
        rtcOfferConstraints: { offerToReceiveAudio: true }
      };
  
      await this.session.answer(options);
  
      // Call is considered established from app POV; media will start after your ACK with SDP answer
      this.updateState({
        isCallActive: true,
        currentCall: { ...this.state.currentCall!, status: 'connected' }
      });
  
      this.setupAudioStreams(this.session);
    } catch (error) {
      this.logger.error("Failed to answer call", { callId, error });
      const err = error instanceof Error ? error : new Error('Failed to answer call');
      this.updateState({ error: err });
      this.config.onError?.(err);
      throw err;
    }
  }
  

  async rejectCall(): Promise<void> {
    const callId = this.session?.id || 'unknown-call';
    
    this.logger.log("Rejecting incoming call", {
      callId,
      event: TaarWebRTCEvents.REJECT_CALL_STARTED
    });
    
    if (!this.session) {
      this.logger.warn("No incoming call to reject", {
        callId,
        event: TaarWebRTCEvents.REJECT_CALL_NO_INCOMING_CALL
      });
      throw new Error('No incoming call to reject');
    }

    try {
      // Reject the incoming call with 486 Busy Here
      await this.session.terminate({
        status_code: 486,
        reason_phrase: 'Busy Here'
      });
      
      this.logger.log("Incoming call rejected successfully", {
        callId,
        statusCode: 486,
        reasonPhrase: 'Busy Here',
        event: TaarWebRTCEvents.REJECT_CALL_SUCCESSFUL
      });
      
      // Update state
      this.updateState({
        isCallActive: false,
        currentCall: undefined
      });
      
      this.session = null;
      
    } catch (error) {
      this.logger.error("Failed to reject incoming call", {
        callId,
        error: error,
        event: TaarWebRTCEvents.REJECT_CALL_FAILED
      });
      
      const err = error instanceof Error ? error : new Error('Failed to reject incoming call');
      this.config.onError?.(err);
      throw err;
    }
  }

  // Add a method to fetch a nonce and trigger onReady
  private async fetchNonceAndSignalReady(): Promise<void> {
    const callId = this.session?.id || 'nonce-fetch';
    
    try {
      const response = await axios.post(`${this.config.prefix}/agent/nonce`);
      const nonce = response.data?.nonce;
      
      if (!nonce) {
        throw new Error('No nonce received from API');
      }
      
      // Optionally store the nonce if needed
      (this as any)._nonce = nonce;
      
      // Call onReady if provided
      if (typeof this.config.onReady === 'function') {
        this.config.onReady(nonce);
      }
    } catch (error) {
      this.logger.error("Failed to fetch nonce", {
        callId,
        error: error
      });
      this.updateState({ error: error instanceof Error ? error : new Error('Failed to fetch nonce') });
      if (typeof this.config.onError === 'function') {
        this.config.onError(error instanceof Error ? error : new Error('Failed to fetch nonce'));
      }
    }
  }
} 